// pages/monitor/panels/ems/EmsSystem.js
// 《云平台（能源管家）需求文档》2.7.7 系统管理（p32–41）
import React, { useState } from 'react';
import ScrollTabs from '../../../../components/ScrollTabs';
import dayjs from 'dayjs';
import { Table, Input, InputNumber, Select, Button, Space, Tag, Switch, Radio, Checkbox, Modal, DatePicker, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { getStation, getDevices, LOGS, TARIFF_ROWS, CORRECTIONS, RECALC_RECORDS, CLEAR_RECORDS, getSecPwdPolicy, SEC_PWD_RECORDS, EMS_RESET_RECORDS, TOPO_PALETTE, TOPO_NODES, TOPO_LINKS, TOPO_SHOW_OPTIONS } from '../../../../config/stationData';
import { HV_NODE_TYPES } from './data';
import { useLang, t } from '../../consoles/ems/langContext';
import './ems.css';

function Panel({ title, sub, extra, children }) {
  return (
    <div className="panel">
      <div className="panel-title"><span>{title}</span>{sub ? <small>{sub}</small> : null}{extra}</div>
      {children}
    </div>
  );
}

// ---------- 1 基本信息管理 ----------
function Basic({ st }) {
  const { lang } = useLang();
  const [form, setForm] = useState({
    name: st.name, location: st.location, onlineAt: st.onlineAt, type: st.typeLabel,
    power: st.rated.power, capacity: st.rated.capacity, voltage: st.grid.voltage, transformer: st.grid.transformer,
  });
  const up = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  return (
    <Panel title={t(lang, '基本信息管理', 'Basic Info Management')} sub={t(lang, '管理站点基本信息数据', 'Manage station basic info data')}
      extra={<Button size="small" className="panel-title-btn" type="primary" onClick={() => message.success(t(lang, '站点基本信息已保存', 'Station basic info saved'))}>{t(lang, '保存', 'Save')}</Button>}>
      <iframe
        title={t(lang, '电站位置地图', 'Station Location Map')}
        src={`https://uri.amap.com/marker?position=120.71,30.79&name=${encodeURIComponent(st.name)}&coordinate=gaode&callnative=false`}
        style={{ width: '100%', height: 260, border: '1px solid rgba(0,212,255,0.25)', borderRadius: 8, marginBottom: 14, background: '#16213a', display: 'block' }}
      />
      <div className="kpi-grid ef2-kv">
        {[
          [t(lang, '电站名称', 'Station Name'), 'name'], [t(lang, '所在区域', 'Region'), 'location'], [t(lang, '投运日期', 'Commissioning Date'), 'onlineAt'], [t(lang, '电站类型', 'Station Type'), 'type'],
          [t(lang, '装机功率', 'Installed Power'), 'power'], [t(lang, '装机容量', 'Installed Capacity'), 'capacity'], [t(lang, '电压等级', 'Voltage Level'), 'voltage'], [t(lang, '变压器', 'Transformer'), 'transformer'],
        ].map(([label, key]) => (
          <div className="dev-kv-row" key={key}>
            <span className="dev-kv-k">{label}</span>
            <Input size="small" value={form[key]} onChange={(e) => up(key, e.target.value)} />
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ---------- 2 系统配置 ----------
function SysConfig({ st }) {
  const { lang } = useLang();
  const dev = getDevices(st.id);
  const [structure, setStructure] = useState({
    pcs: dev.pcs.length, rack: dev.racks.length, hvac: dev.hvac.length, meter: dev.meters.length, fire: dev.fire.length,
  });
  return (
    <Panel title={t(lang, '系统配置', 'System Configuration')} sub={t(lang, '配置站点能量结构', 'Configure station energy structure')}>
      <Table
        size="small" rowKey="k" pagination={false}
        columns={[
          { title: t(lang, '组件', 'Component'), dataIndex: 'k' },
          { title: t(lang, '数量', 'Quantity'), dataIndex: 'v', align: 'right', render: (v, r) => <InputNumber size="small" value={v} onChange={(x) => setStructure((s) => ({ ...s, [r.key]: x }))} style={{ width: 110 }} /> },
          { title: t(lang, '启用', 'Enabled'), dataIndex: 'on', align: 'center', render: (v, r) => <Switch size="small" checked={v > 0} onChange={(c) => setStructure((s) => ({ ...s, [r.key]: c ? 1 : 0 }))} /> },
        ]}
        dataSource={[
          { key: 'pcs', k: t(lang, 'PCS 储能变流器', 'PCS Converter'), v: structure.pcs, on: structure.pcs },
          { key: 'rack', k: t(lang, '电池堆 / 电池簇', 'Battery Pack / Cluster'), v: structure.rack, on: structure.rack },
          { key: 'hvac', k: t(lang, '空调', 'AC Unit'), v: structure.hvac, on: structure.hvac },
          { key: 'meter', k: t(lang, '电表', 'Meter'), v: structure.meter, on: structure.meter },
          { key: 'fire', k: t(lang, '消防设备', 'Fire Fighting Equipment'), v: structure.fire, on: structure.fire },
        ]}
      />
      <div className="ems-inline" style={{ marginTop: 14 }}>
        <Button type="primary" onClick={() => message.success(t(lang, '能量结构配置已保存', 'Energy structure config saved'))}>{t(lang, '保存配置', 'Save Config')}</Button>
      </div>
    </Panel>
  );
}

// ---------- 3 设备管理 ----------
function DeviceManage({ st }) {
  const { lang } = useLang();
  const dev = getDevices(st.id);
  const [meters, setMeters] = useState(() => dev.meters);
  const [modal, setModal] = useState(null);
  const [newMeter, setNewMeter] = useState({ name: '', ratio: 200 });
  const rows = [
    ...dev.pcs.map((p) => ({ key: p.id, type: 'PCS', name: p.name, state: p.running ? '运行' : '中断', detail: `${p.p} kW` })),
    ...dev.racks.map((r) => ({ key: r.id, type: 'BMS', name: r.name, state: r.state, detail: `SOC ${r.soc}%` })),
    ...meters.map((m) => ({ key: m.id, type: '电表', name: m.name, state: m.state, detail: `${t(lang, '电能', 'Energy')} ${m.ep} kWh` })),
    ...dev.hvac.map((h) => ({ key: h.id, type: '空调', name: h.name, state: h.state, detail: `${h.setTemp} ℃` })),
    ...dev.fire.map((f) => ({ key: f.id, type: '消防', name: f.name, state: f.state, detail: t(lang, f.cat) })),
  ];
  return (
    <>
      <Panel title={t(lang, '设备管理', 'Device Management')} sub={t(lang, '站点设备管理', 'Station device management')}>
        <Table
          size="small" rowKey="key" pagination={{ pageSize: 12, showSizeChanger: false }}
          columns={[
            { title: t(lang, '设备类型', 'Device Type'), dataIndex: 'type', render: (v) => t(lang, v) },
            { title: t(lang, '设备名称', 'Device Name'), dataIndex: 'name' },
            { title: t(lang, '状态', 'Status'), dataIndex: 'state', render: (v) => <Tag color={v === '正常' || v === '运行' ? 'green' : 'gold'}>{t(lang, v)}</Tag> },
            { title: t(lang, '运行数据', 'Running Data'), dataIndex: 'detail' },
          ]}
          dataSource={rows}
        />
      </Panel>

      <Panel title={t(lang, '电表管理', 'Meter Management')} sub={t(lang, '电表更换 / 新增电表（电能示数比）', 'Replace meter / add meter (energy reading ratio)')}
        extra={<Space><Button size="small" className="panel-title-btn" onClick={() => setModal('replace')}>{t(lang, '更换电表', 'Replace Meter')}</Button><Button size="small" className="panel-title-btn" type="primary" onClick={() => setModal('add')}>{t(lang, '新增电表', 'Add Meter')}</Button></Space>}>
        <Table
          size="small" rowKey="id" pagination={false}
          columns={[
            { title: t(lang, '电表编号', 'Meter No.'), dataIndex: 'id' },
            { title: t(lang, '电表名称', 'Meter Name'), dataIndex: 'name' },
            { title: t(lang, '通信状态', 'Comm Status'), dataIndex: 'state', render: (v) => <Tag color={v === '正常' ? 'green' : 'gold'}>{t(lang, v)}</Tag> },
            { title: t(lang, '电能示数 (kWh)', 'Energy Reading (kWh)'), dataIndex: 'ep', align: 'right' },
            { title: t(lang, '电能示数比', 'Reading Ratio'), align: 'right', render: () => 200 },
          ]}
          dataSource={meters}
        />
      </Panel>

      <Modal
        title={modal === 'add' ? t(lang, '新增电表', 'Add Meter') : t(lang, '更换电表', 'Replace Meter')}
        open={!!modal}
        onCancel={() => setModal(null)}
        okText={t(lang, '提交', 'Submit')}
        cancelText={t(lang, '取消', 'Cancel')}
        onOk={() => {
          if (modal === 'add') {
            setMeters((arr) => [...arr, { id: `M-${String(arr.length + 1).padStart(2, '0')}`, name: newMeter.name || '新增电表', u: [0, 0, 0], i: [0, 0, 0], p: 0, q: 0, pf: 1, ep: 0, state: '正常', freq: 50, wiring: '三相四线' }]);
          }
          setModal(null);
          setNewMeter({ name: '', ratio: 200 });
          message.success(modal === 'add' ? t(lang, '新增电表成功', 'Meter added successfully') : t(lang, '电表更换成功', 'Meter replaced successfully'));
        }}
      >
        <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '电表名称', 'Meter Name')}</span><Input value={newMeter.name} onChange={(e) => setNewMeter((s) => ({ ...s, name: e.target.value }))} /></div>
        <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '电能示数比', 'Reading Ratio')}</span><InputNumber value={newMeter.ratio} onChange={(v) => setNewMeter((s) => ({ ...s, ratio: v }))} addonAfter={t(lang, '倍', 'x')} style={{ width: 180 }} /></div>
      </Modal>
    </>
  );
}

// ---------- 强电接线图（共用） ----------
function HvDiagram() {
  const { lang } = useLang();
  return (
    <div className="wg-wrap">
      <svg viewBox="0 0 1000 260" className="wg-svg" style={{ minWidth: 820 }} role="img" aria-label={t(lang, '强电接线图', 'HV Wiring Diagram')}>
        <line x1={60} y1={140} x2={940} y2={140} className="wg-bus" />
        <text x={72} y={130} className="wg-tip">{t(lang, '交流母线', 'AC Bus')}</text>
        <g className="wg-node"><rect x={40} y={100} width={140} height={58} rx={10} fill="rgba(13,31,68,0.85)" stroke="rgba(0,180,216,0.45)" /><text x={110} y={122} textAnchor="middle" className="wg-nm">{t(lang, '公共连接点')}</text><text x={110} y={142} textAnchor="middle" className="wg-val">{t(lang, '并网点', 'Grid Connection Point')}</text></g>
        <line x1={180} y1={129} x2={250} y2={129} className="wg-flow" />
        <g className="wg-node"><rect x={250} y={100} width={160} height={58} rx={10} fill="rgba(13,31,68,0.85)" stroke="rgba(0,180,216,0.45)" /><text x={330} y={122} textAnchor="middle" className="wg-nm">{t(lang, '变压器节点')}</text><text x={330} y={142} textAnchor="middle" className="wg-val">10/0.4 kV</text></g>
        <line x1={410} y1={129} x2={470} y2={129} className="wg-flow" />
        <line x1={470} y1={140} x2={470} y2={140} className="wg-flow" />
        <line x1={470} y1={140} x2={470} y2={210} className="wg-flow" />
        <g className="wg-node"><rect x={400} y={210} width={160} height={44} rx={10} fill="rgba(13,31,68,0.85)" stroke="rgba(0,180,216,0.45)" /><text x={480} y={236} textAnchor="middle" className="wg-nm">{t(lang, '储能节点')}</text></g>
        <line x1={560} y1={140} x2={620} y2={140} className="wg-flow" />
        <g className="wg-node"><rect x={620} y={100} width={150} height={58} rx={10} fill="rgba(13,31,68,0.85)" stroke="rgba(0,180,216,0.45)" /><text x={695} y={122} textAnchor="middle" className="wg-nm">{t(lang, '负载节点')}</text><text x={695} y={142} textAnchor="middle" className="wg-val">{t(lang, '园区负载', 'Park Load')}</text></g>
        <line x1={770} y1={129} x2={820} y2={129} className="wg-flow" />
        <g className="wg-node"><rect x={820} y={100} width={150} height={58} rx={10} fill="rgba(13,31,68,0.85)" stroke="rgba(0,180,216,0.35)" strokeDasharray="5 4" /><text x={895} y={122} textAnchor="middle" className="wg-nm">{t(lang, '光伏节点')}</text><text x={895} y={142} textAnchor="middle" className="wg-val">{t(lang, '未配置', 'Not Configured')}</text></g>
      </svg>
    </div>
  );
}

// ---------- 4 强电管理（对齐需求文档 p37–40：设备台账表 + 新增/编辑节点 + 更换电表 + 电池信息） ----------
function HvManage({ st }) {
  const { lang } = useLang();
  const dev = getDevices(st.id);
  // p037_i056 设备台账：序号 / 并网点 / 单元 / 设备类型 / 设备名称 / 厂商 / 型号 / 操作
  const [rows, setRows] = useState([
    { key: 1, seq: 1, grid: '1#并网点', unit: '1#储能单元', type: 'PCS', name: '1#PCS', vendor: '', model: '', extra: '' },
    { key: 2, seq: 2, grid: '1#并网点', unit: '1#储能单元', type: 'BMS电池堆', name: '1#BMS电池堆', vendor: '', model: '', extra: 'battery' },
    { key: 3, seq: 3, grid: '1#并网点', unit: '1#储能单元', type: '空调', name: '1#空调', vendor: '', model: '', extra: '' },
    { key: 4, seq: 4, grid: '1#并网点', unit: '1#储能单元', type: '双向电表', name: '1#储能表', vendor: '', model: '', extra: 'meter' },
    { key: 5, seq: 5, grid: '1#并网点', unit: '1#储能单元', type: '双向电表', name: '1#总表', vendor: '', model: '', extra: '' },
    { key: 6, seq: 6, grid: '1#并网点', unit: '1#储能单元', type: '双向电表', name: '1#负载表', vendor: '', model: '', extra: '' },
    { key: 7, seq: 7, grid: '1#并网点', unit: '1#储能单元', type: '液冷机组', name: '1', vendor: '', model: '', extra: '' },
    { key: 8, seq: 8, grid: '1#并网点', unit: '1#储能单元', type: '消防主机', name: '1123', vendor: '', model: '', extra: '' },
  ]);
  const nodeTypeOf = (t) => (t === 'PCS' || t === 'BMS电池堆' ? '储能节点' : t === '双向电表' ? '公共连接点' : '负载节点');
  const [editing, setEditing] = useState(null); // null 关闭；'add' 新增；行 key=编辑
  const blank = (type) => ({
    type,
    name: type === '公共连接点' ? '公共连接点' : `新建${type.replace('节点', '')}`,
    meter: dev.meters[0]?.name || '1#总表', ipc: '', cap: 200, battery: '磷酸铁锂',
    pcs: dev.pcs[0]?.name || '1#PCS', pcsBr: '支路1',
    rack: dev.racks[0]?.name || '1#电池堆', clu: '1#电池簇',
    chgEff: 99, disEff: 99, pwrLo: type === '变压器节点' ? -1000 : -100, pwrHi: type === '变压器节点' ? 1000 : 100,
    socLo: 20, socHi: 90, cv: false, cvV: '', cvF: '', cvC: '', cvI: '', cvI2: '',
    tv: true, tvLo: 33, tvHi: 66, cellV: true, cellVLo: '', cellVHi: '', cellT: false, cellTLo: '', cellTHi: '',
    ctrl: '不可控', effFwd: 99, effRev: 99,
  });
  const [form, setForm] = useState(() => blank('公共连接点'));
  const up = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // p037_i057 更换电表弹窗（老电表终结时间 / 新电表名称 / 新电表接入时间）
  const [meterTarget, setMeterTarget] = useState(null);
  const [meterForm, setMeterForm] = useState({ end: '2023-12-05', name: '', access: '2023-12-05' });
  // p037_i056 BMS 行「电池信息」弹窗
  const [batteryTarget, setBatteryTarget] = useState(null);

  const brOpts = ['支路1', '支路2', '支路3'].map((v) => ({ value: v, label: t(lang, v, { '支路1': 'Branch 1', '支路2': 'Branch 2', '支路3': 'Branch 3' }[v]) }));
  const cluOpts = ['1#电池簇', '2#电池簇', '3#电池簇'].map((v) => ({ value: v, label: t(lang, v, { '1#电池簇': 'Battery Cluster 1#', '2#电池簇': 'Battery Cluster 2#', '3#电池簇': 'Battery Cluster 3#' }[v]) }));
  const batOpts = ['磷酸铁锂', '铅酸电池', '三元锂'].map((v) => ({ value: v, label: t(lang, v, { '磷酸铁锂': 'LFP', '铅酸电池': 'Lead-Acid Battery', '三元锂': 'Ternary Lithium' }[v]) }));

  const renderFields = (n) => (
    <>
      <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '节点类型', 'Node Type')}</span>
        <Select size="small" value={n.type} onChange={(v) => up('type', v)} style={{ width: 220 }} options={HV_NODE_TYPES.map((nt) => ({ value: nt.name, label: t(lang, nt.name) }))} />
      </div>
      <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '节点名称', 'Node Name')}</span>
        <Input size="small" value={n.name} onChange={(e) => up('name', e.target.value)} style={{ width: 220 }} />
      </div>
      {n.type === '储能节点' || n.type === '光伏节点' ? (
        <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '工控机编号', 'IPC No.')}</span>
          <Input size="small" value={n.ipc} onChange={(e) => up('ipc', e.target.value)} style={{ width: 220 }} />
        </div>
      ) : null}
      <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '功率参考', 'Power Reference')}</span>
        <Select size="small" value={n.meter} onChange={(v) => up('meter', v)} style={{ width: 220 }}
          options={dev.meters.map((m) => ({ value: m.name, label: m.name }))} />
      </div>
      {n.type === '储能节点' ? (
        <>
          <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '单元容量', 'Unit Capacity')}</span>
            <InputNumber size="small" value={n.cap} onChange={(v) => up('cap', v)} addonAfter="kWh" style={{ width: 220 }} />
          </div>
          <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '电池类型', 'Battery Type')}</span>
            <Select size="small" value={n.battery} onChange={(v) => up('battery', v)} style={{ width: 220 }} options={batOpts} />
          </div>
          <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '关联设备 - PCS', 'Related Device - PCS')}</span>
            <Select size="small" value={n.pcs} onChange={(v) => up('pcs', v)} style={{ width: 120 }} options={dev.pcs.map((p) => ({ value: p.name, label: p.name }))} />
            <span style={{ margin: '0 6px', color: '#8fa3bf' }}>↔</span>
            <Select size="small" value={n.pcsBr} onChange={(v) => up('pcsBr', v)} style={{ width: 90 }} options={brOpts} />
          </div>
          <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '关联设备 - 电池堆', 'Related Device - Battery Pack')}</span>
            <Select size="small" value={n.rack} onChange={(v) => up('rack', v)} style={{ width: 120 }} options={dev.racks.map((r) => ({ value: r.name, label: r.name }))} />
            <span style={{ margin: '0 6px', color: '#8fa3bf' }}>↔</span>
            <Select size="small" value={n.clu} onChange={(v) => up('clu', v)} style={{ width: 90 }} options={cluOpts} />
          </div>
          <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '充放电效率', 'Charge/Discharge Efficiency')}</span>
            <InputNumber size="small" value={n.chgEff} onChange={(v) => up('chgEff', v)} addonAfter="%" style={{ width: 110 }} />
            <span style={{ margin: '0 6px', color: '#8fa3bf' }}>~</span>
            <InputNumber size="small" value={n.disEff} onChange={(v) => up('disEff', v)} addonAfter="%" style={{ width: 110 }} />
          </div>
          <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '电气保护', 'Electrical Protection')}</span>
            <InputNumber size="small" value={n.pwrLo} onChange={(v) => up('pwrLo', v)} addonAfter="kW" style={{ width: 110 }} />
            <span style={{ margin: '0 6px', color: '#8fa3bf' }}>~</span>
            <InputNumber size="small" value={n.pwrHi} onChange={(v) => up('pwrHi', v)} addonAfter="kW" style={{ width: 110 }} />
          </div>
          <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, 'SOC 保护', 'SOC Protection')}</span>
            <InputNumber size="small" value={n.socLo} onChange={(v) => up('socLo', v)} addonAfter="%" style={{ width: 110 }} />
            <span style={{ margin: '0 6px', color: '#8fa3bf' }}>~</span>
            <InputNumber size="small" value={n.socHi} onChange={(v) => up('socHi', v)} addonAfter="%" style={{ width: 110 }} />
          </div>
          <div className="dev-kv-row"><span className="dev-kv-k"><Checkbox checked={n.cv} onChange={(e) => up('cv', e.target.checked)}>{t(lang, '恒压保护', 'Constant Voltage Protection')}</Checkbox></span>
            {n.cv ? (
              <Space wrap>
                <Input size="small" placeholder={t(lang, '均充电压', 'Equalize Voltage')} value={n.cvV} onChange={(e) => up('cvV', e.target.value)} style={{ width: 110 }} />
                <Input size="small" placeholder={t(lang, '浮充电压', 'Float Voltage')} value={n.cvF} onChange={(e) => up('cvF', e.target.value)} style={{ width: 110 }} />
                <Input size="small" placeholder={t(lang, '截止电压', 'Cut-off Voltage')} value={n.cvC} onChange={(e) => up('cvC', e.target.value)} style={{ width: 110 }} />
                <Input size="small" placeholder={t(lang, '均充转浮充电流', 'Equalize-to-Float Current')} value={n.cvI} onChange={(e) => up('cvI', e.target.value)} style={{ width: 130 }} />
                <Input size="small" placeholder={t(lang, '浮充转快充电流', 'Float-to-Fast-Charge Current')} value={n.cvI2} onChange={(e) => up('cvI2', e.target.value)} style={{ width: 130 }} />
              </Space>
            ) : null}
          </div>
          <div className="dev-kv-row"><span className="dev-kv-k"><Checkbox checked={n.tv} onChange={(e) => up('tv', e.target.checked)}>{t(lang, '总电压保护', 'Total Voltage Protection')}</Checkbox></span>
            {n.tv ? (
              <Space>
                <InputNumber size="small" value={n.tvLo} onChange={(v) => up('tvLo', v)} addonAfter="V" style={{ width: 110 }} />
                <span style={{ color: '#8fa3bf' }}>~</span>
                <InputNumber size="small" value={n.tvHi} onChange={(v) => up('tvHi', v)} addonAfter="V" style={{ width: 110 }} />
              </Space>
            ) : null}
          </div>
          <div className="dev-kv-row"><span className="dev-kv-k"><Checkbox checked={n.cellV} onChange={(e) => up('cellV', e.target.checked)}>{t(lang, '单体电压保护', 'Cell Voltage Protection')}</Checkbox></span>
            {n.cellV ? (
              <Space>
                <Input size="small" placeholder={t(lang, '下限', 'Lower Limit')} value={n.cellVLo} onChange={(e) => up('cellVLo', e.target.value)} style={{ width: 110 }} />
                <span style={{ color: '#8fa3bf' }}>~</span>
                <Input size="small" placeholder={t(lang, '上限', 'Upper Limit')} value={n.cellVHi} onChange={(e) => up('cellVHi', e.target.value)} style={{ width: 110 }} />
              </Space>
            ) : null}
          </div>
          <div className="dev-kv-row"><span className="dev-kv-k"><Checkbox checked={n.cellT} onChange={(e) => up('cellT', e.target.checked)}>{t(lang, '单体温度保护', 'Cell Temperature Protection')}</Checkbox></span>
            {n.cellT ? (
              <Space>
                <Input size="small" placeholder={t(lang, '下限', 'Lower Limit')} value={n.cellTLo} onChange={(e) => up('cellTLo', e.target.value)} style={{ width: 110 }} />
                <span style={{ color: '#8fa3bf' }}>~</span>
                <Input size="small" placeholder={t(lang, '上限', 'Upper Limit')} value={n.cellTHi} onChange={(e) => up('cellTHi', e.target.value)} style={{ width: 110 }} />
              </Space>
            ) : null}
          </div>
        </>
      ) : null}
      {n.type === '变压器节点' ? (
        <>
          <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '电气保护', 'Electrical Protection')}</span>
            <InputNumber size="small" value={n.pwrLo} onChange={(v) => up('pwrLo', v)} addonAfter="kW" style={{ width: 110 }} />
            <span style={{ margin: '0 6px', color: '#8fa3bf' }}>~</span>
            <InputNumber size="small" value={n.pwrHi} onChange={(v) => up('pwrHi', v)} addonAfter="kW" style={{ width: 110 }} />
          </div>
          <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '转换效率', 'Conversion Efficiency')}</span>
            <InputNumber size="small" value={n.effFwd} onChange={(v) => up('effFwd', v)} addonAfter="%" style={{ width: 110 }} />
            <span style={{ margin: '0 6px', color: '#8fa3bf' }}>~</span>
            <InputNumber size="small" value={n.effRev} onChange={(v) => up('effRev', v)} addonAfter="%" style={{ width: 110 }} />
          </div>
        </>
      ) : null}
      {n.type === '光伏节点' ? (
        <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '可控性', 'Controllability')}</span>
          <Radio.Group value={n.ctrl} onChange={(e) => up('ctrl', e.target.value)}>
            <Radio value="不可控">{t(lang, '不可控', 'Uncontrollable')}</Radio>
            <Radio value="柔性可控">{t(lang, '柔性可控', 'Flexibly Controllable')}</Radio>
            <Radio value="硬性可控">{t(lang, '硬性可控', 'Rigidly Controllable')}</Radio>
          </Radio.Group>
        </div>
      ) : null}
    </>
  );

  const links = [
    { key: 1, from: '公共连接点', to: '变压器节点', type: '电气连接' },
    { key: 2, from: '变压器节点', to: '储能节点', type: '电气连接' },
    { key: 3, from: '变压器节点', to: '负载节点', type: '电气连接' },
    { key: 4, from: '变压器节点', to: '光伏节点', type: '未配置' },
  ];

  return (
    <>
      <Panel title={t(lang, '强电结构', 'HV Structure')} sub={t(lang, '设备台账 · 并网点 / 单元 / 设备类型 / 设备名称 / 厂商 / 型号', 'Equipment Ledger · Grid Point / Unit / Device Type / Device Name / Vendor / Model')}
        extra={<Button size="small" className="panel-title-btn" type="primary" icon={<PlusOutlined />} onClick={() => { setForm(blank('公共连接点')); setEditing('add'); }}>{t(lang, '新增节点', 'Add Node')}</Button>}>
        <Table
          size="small" rowKey="key" pagination={false} dataSource={rows}
          columns={[
            { title: t(lang, '序号', 'No.'), dataIndex: 'seq', width: 60 },
            { title: t(lang, '并网点', 'Grid Point'), dataIndex: 'grid', width: 120 },
            { title: t(lang, '单元', 'Unit'), dataIndex: 'unit', width: 140 },
            { title: t(lang, '设备类型', 'Device Type'), dataIndex: 'type', width: 110, render: (v) => t(lang, v) },
            { title: t(lang, '设备名称', 'Device Name'), dataIndex: 'name', width: 150 },
            { title: t(lang, '厂商', 'Vendor'), dataIndex: 'vendor', render: (v) => v || '-' },
            { title: t(lang, '型号', 'Model'), dataIndex: 'model', render: (v) => v || '-' },
            {
              title: t(lang, '操作', 'Actions'), width: 230,
              render: (_, r) => (
                <Space size={0}>
                  <Button type="link" size="small" onClick={() => { setForm({ ...blank(nodeTypeOf(r.type)), name: r.name }); setEditing(r.key); }}>{t(lang, '编辑', 'Edit')}</Button>
                  <Button type="link" size="small" onClick={() => { setRows((arr) => arr.filter((x) => x.key !== r.key)); message.success(t(lang, '节点已删除（演示）', 'Node deleted (demo)')); }}>{t(lang, '删除', 'Delete')}</Button>
                  {r.extra === 'battery' ? <Button type="link" size="small" onClick={() => setBatteryTarget(r)}>{t(lang, '电池信息', 'Battery Info')}</Button> : null}
                  {r.extra === 'meter' ? <Button type="link" size="small" onClick={() => { setMeterTarget(r); setMeterForm((s) => ({ ...s, name: '' })); }}>{t(lang, '更换电表', 'Replace Meter')}</Button> : null}
                </Space>
              ),
            },
          ]}
        />
      </Panel>

      <Panel title={t(lang, '强电接线表', 'HV Wiring Table')} sub={t(lang, '节点之间的连接关系', 'Connection relationships between nodes')}>
        <Table
          size="small" rowKey="key" pagination={false}
          columns={[
            { title: t(lang, '起始节点', 'Source Node'), dataIndex: 'from', render: (v) => t(lang, v) },
            { title: t(lang, '目标节点', 'Target Node'), dataIndex: 'to', render: (v) => t(lang, v) },
            { title: t(lang, '连接类型', 'Connection Type'), dataIndex: 'type', render: (v) => <Tag color={v === '未配置' ? 'default' : 'blue'}>{v === '未配置' ? t(lang, '未配置', 'Not Configured') : t(lang, '电气连接', 'Electrical Connection')}</Tag> },
          ]}
          dataSource={links}
        />
      </Panel>

      <Panel title={t(lang, '强电接线图', 'HV Wiring Diagram')} sub={t(lang, '按强电结构生成', 'Generated from HV structure')}>
        <HvDiagram />
      </Panel>

      {/* 新增节点 / 编辑节点（p037_i058 / p038_i059 / p039_i060 / p040_i061 / p040_i062） */}
      <Modal
        title={editing === 'add' ? t(lang, '新增节点', 'Add Node') : t(lang, '编辑节点', 'Edit Node')}
        open={!!editing}
        onCancel={() => setEditing(null)}
        okText={t(lang, '确定', 'OK')}
        cancelText={t(lang, '取消', 'Cancel')}
        width={720}
        onOk={() => {
          if (editing === 'add') {
            setRows((arr) => [...arr, { key: arr.length + 1, seq: arr.length + 1, grid: '1#并网点', unit: '1#储能单元', type: form.type, name: form.name, vendor: '', model: '', extra: '' }]);
          } else {
            setRows((arr) => arr.map((x) => (x.key === editing ? { ...x, type: form.type, name: form.name } : x)));
          }
          setEditing(null);
          message.success(editing === 'add' ? t(lang, '节点已新增（演示）', 'Node added (demo)') : t(lang, '节点已保存（演示）', 'Node saved (demo)'));
        }}
      >
        {renderFields(form)}
      </Modal>

      {/* 更换电表（p037_i057） */}
      <Modal
        title={t(lang, '更换电表', 'Replace Meter')}
        open={!!meterTarget}
        onCancel={() => setMeterTarget(null)}
        okText={t(lang, '确定', 'OK')}
        cancelText={t(lang, '取消', 'Cancel')}
        width={520}
        onOk={() => { setMeterTarget(null); message.success(t(lang, `电表已更换为「${meterForm.name || '新电表'}」（演示）`, `Meter replaced with "${meterForm.name || '新电表'}" (demo)`)); }}
      >
        <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '老电表终结时间', 'Old Meter End Time')}</span>
          <DatePicker size="small" style={{ width: 220 }} defaultValue={dayjs('2023-12-05')} />
        </div>
        <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '新电表名称', 'New Meter Name')}</span>
          <Input size="small" value={meterForm.name} onChange={(e) => setMeterForm((s) => ({ ...s, name: e.target.value }))} style={{ width: 220 }} placeholder={t(lang, '请输入', 'Please enter')} />
        </div>
        <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '新电表接入时间', 'New Meter Access Time')}</span>
          <DatePicker size="small" style={{ width: 220 }} defaultValue={dayjs('2023-12-05')} />
        </div>
      </Modal>

      {/* 电池信息（p037_i056） */}
      <Modal
        title={t(lang, '电池信息', 'Battery Info')}
        open={!!batteryTarget}
        onCancel={() => setBatteryTarget(null)}
        footer={<Button size="small" type="primary" onClick={() => setBatteryTarget(null)}>{t(lang, '关闭', 'Close')}</Button>}
        width={520}
      >
        {batteryTarget ? (
          <div className="kpi-grid ef2-kv">
            <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '电池堆', 'Battery Pack')}</span><span className="dev-kv-v">{batteryTarget.name}</span></div>
            <div className="dev-kv-row"><span className="dev-kv-k">SOC</span><span className="dev-kv-v">72%</span></div>
            <div className="dev-kv-row"><span className="dev-kv-k">SOH</span><span className="dev-kv-v">98%</span></div>
            <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '单体电压', 'Cell Voltage')}</span><span className="dev-kv-v">3.22 V</span></div>
            <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '单体温度', 'Cell Temperature')}</span><span className="dev-kv-v">26.8 ℃</span></div>
          </div>
        ) : null}
      </Modal>
    </>
  );
}

// ---------- 5 拓扑图配置 ----------
function TopoConfig() {
  const { lang } = useLang();
  const [nodes, setNodes] = useState(TOPO_NODES);
  const [show, setShow] = useState(TOPO_SHOW_OPTIONS);
  const pos = new Map(nodes.map((n) => [n.key, n]));
  const TOPO_NAME_EN = { '电网': 'Grid', '变压器': 'Transformer', '电池簇': 'Battery Cluster', '电网 10kV': 'Grid 10kV', '变压器 1250kVA': 'Transformer 1250kVA', '并网母线': 'Grid Bus', '电池堆 RACK-01': 'Battery Pack RACK-01', '园区负载': 'Park Load' };
  const TOPO_SHOW_EN = { '有功功率': 'Active Power', '电压': 'Voltage', '电流': 'Current', '温度': 'Temperature', '运行状态': 'Running State', '告警标识': 'Alarm Indicator' };
  return (
    <Panel title={t(lang, '拓扑图配置', 'Topology Config')} sub={t(lang, '配置强电结构 · 拖拽图元并绑定设备', 'Configure HV structure · drag elements and bind devices')}
      extra={<Button size="small" className="panel-title-btn" type="primary" onClick={() => message.success(t(lang, '拓扑图配置已保存', 'Topology config saved'))}>{t(lang, '保存', 'Save')}</Button>}>
      <div className="ems-inline">
        <span className="ems-inline-label">{t(lang, '图元调色板', 'Element Palette')}</span>
        {TOPO_PALETTE.map((p) => <Tag key={p.key} color="blue">{t(lang, p.name, TOPO_NAME_EN[p.name])}</Tag>)}
      </div>
      <div className="ems-inline">
        <span className="ems-inline-label">{t(lang, '展示数据项', 'Display Data Items')}</span>
        <Space wrap>
          {show.map((s) => (
            <Tag.CheckableTag key={s.key} checked={s.checked}
              onChange={(c) => setShow((arr) => arr.map((x) => (x.key === s.key ? { ...x, checked: c } : x)))}>
              {t(lang, s.label, TOPO_SHOW_EN[s.label])}
            </Tag.CheckableTag>
          ))}
        </Space>
      </div>
      <div className="wg-wrap">
        <svg viewBox="0 0 820 320" className="wg-svg" style={{ minWidth: 700 }} role="img" aria-label={t(lang, '拓扑图配置画布', 'Topology Config Canvas')}>
          {TOPO_LINKS.map((l) => {
            const a = pos.get(l.from);
            const b = pos.get(l.to);
            if (!a || !b) return null;
            return <line key={`${l.from}-${l.to}`} x1={a.x + 60} y1={a.y + 22} x2={b.x + 60} y2={b.y + 22} className="wg-flow" />;
          })}
          {nodes.map((n) => (
            <g key={n.key} className="wg-node">
              <rect x={n.x} y={n.y} width={120} height={44} rx={8} fill="rgba(13,31,68,0.85)" stroke="rgba(0,180,216,0.45)" />
              <text x={n.x + 60} y={n.y + 26} textAnchor="middle" className="wg-nm">{t(lang, n.name, TOPO_NAME_EN[n.name])}</text>
            </g>
          ))}
          <text x={30} y={310} className="wg-tip">{t(lang, '提示：在后台拖拽左侧图元到画布，并绑定设备以展示相关数据。', 'Tip: drag elements from the left onto the canvas in the backend and bind devices to display related data.')}</text>
        </svg>
      </div>
      <div className="ems-inline" style={{ marginTop: 10 }}>
        <Button size="small" onClick={() => setNodes(TOPO_NODES)}>{t(lang, '重置布局', 'Reset Layout')}</Button>
      </div>
    </Panel>
  );
}

// ---------- 6 日志管理 ----------
function Logs() {
  const { lang } = useLang();
  const [type, setType] = useState('全部');
  const rows = type === '全部' ? LOGS : LOGS.filter((l) => l.type === type);
  const LOG_TYPE_EN = { '全部': 'All', '操作日志': 'Operation Logs', '告警日志': 'Alarm Logs', '登录日志': 'Login Logs' };
  return (
    <Panel title={t(lang, '日志管理', 'Log Management')} sub={t(lang, '记录重要操作日志', 'Records important operation logs')}>
      <div className="ems-inline">
        <span className="ems-inline-label">{t(lang, '日志类型', 'Log Type')}</span>
        <Select value={type} onChange={setType} style={{ width: 160 }} options={['全部', '操作日志', '告警日志', '登录日志'].map((v) => ({ value: v, label: t(lang, v, LOG_TYPE_EN[v]) }))} />
      </div>
      <Table
        size="small" rowKey="key" pagination={false}
        columns={[
          { title: t(lang, '时间', 'Time'), dataIndex: 'time' },
          { title: t(lang, '类型', 'Type'), dataIndex: 'type', render: (v) => <Tag color="blue">{v}</Tag> },
          { title: t(lang, '用户', 'User'), dataIndex: 'user' },
          { title: t(lang, '操作', 'Action'), dataIndex: 'action' },
          { title: t(lang, '对象', 'Target'), dataIndex: 'target' },
          { title: t(lang, '结果', 'Result'), dataIndex: 'result', render: (v) => <Tag color={v === '成功' ? 'green' : v === '失败' ? 'red' : 'default'}>{v}</Tag> },
          { title: 'IP', dataIndex: 'ip' },
        ]}
        dataSource={rows}
      />
    </Panel>
  );
}

// ---------- 7 EMS 时段管理 ----------
const EMS_PERIOD_CHANGES = [
  { key: 1, time: '2026-09-28 14:20', user: 'admin', content: '谷时充电时段 00:00-06:00 调整为 00:00-07:00' },
  { key: 2, time: '2026-08-15 09:10', user: 'ops01', content: '新增尖时放电时段 19:00-22:00' },
];

// 时段类型与颜色（图例：不计费灰 / 尖红 / 峰橙 / 平蓝 / 谷绿 / 深谷青）
const PERIOD_KINDS = ['不计费', '尖', '峰', '平', '谷', '深谷'];
const KIND_COLOR = { 不计费: '#7a8699', 尖: '#ff4d4f', 峰: '#ffa940', 平: '#4dd8ff', 谷: '#52e8b0', 深谷: '#2fd8c9' };

// 当前 EMS 时段（截图：00:00-08:00 谷 / 08:00-11:00 峰 / 11:00-13:00 谷 / 13:00-17:00 峰 / 17:00-24:00 平）
const DEFAULT_PERIODS = [
  { key: 1, start: '00:00', end: '08:00', kind: '谷' },
  { key: 2, start: '08:00', end: '11:00', kind: '峰' },
  { key: 3, start: '11:00', end: '13:00', kind: '谷' },
  { key: 4, start: '13:00', end: '17:00', kind: '峰' },
  { key: 5, start: '17:00', end: '24:00', kind: '平' },
];

const toMin = (t) => { const [h, m] = String(t).split(':').map(Number); return h * 60 + (m || 0); };
const segPct = (start, end) => Math.max(0, ((toMin(end) - toMin(start)) / 1440) * 100);

function EmsPeriods() {
  const { lang } = useLang();
  const [tab, setTab] = useState('current');
  const [current] = useState(DEFAULT_PERIODS);
  const [manage, setManage] = useState(DEFAULT_PERIODS.map((p) => ({ ...p })));
  const [cfg, setCfg] = useState(null); // 'single' | 'batch'
  const up = (setter, key, patch) => setter((arr) => arr.map((x) => (x.key === key ? { ...x, ...patch } : x)));

  return (
    <>
      {/* 子页切换对齐光储充「控制中心」顶部胶囊 Tabs */}
      <ScrollTabs
        activeKey={tab}
        onChange={setTab}
        items={[
        {
          key: 'current',
          label: t(lang, '当前EMS时段', 'Current EMS Period'),
          children: (
            <>
              <div className="ems-period-axis">
                {current.map((p) => (
                  <div key={p.key} className="ems-period-seg" style={{ width: `${segPct(p.start, p.end)}%`, background: KIND_COLOR[p.kind] }}>
                    <span className="ems-period-seg-name">{t(lang, p.kind)}</span>
                    <span className="ems-period-seg-time">{p.start}-{p.end}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom: 14 }}>
                {PERIOD_KINDS.map((k) => (
                  <span key={k} className="ems-period-legend"><i className="ems-period-legend-dot" style={{ background: KIND_COLOR[k] }} />{t(lang, k)}</span>
                ))}
              </div>
              <Table
                size="small" rowKey="key" pagination={false}
                columns={[
                  { title: t(lang, '开始时间', 'Start Time'), dataIndex: 'start' },
                  { title: t(lang, '结束时间', 'End Time'), dataIndex: 'end' },
                  { title: t(lang, '时段说明', 'Period Description'), dataIndex: 'kind', render: (v) => <span style={{ color: KIND_COLOR[v], fontWeight: 600 }}>{t(lang, v)}</span> },
                ]}
                dataSource={current}
              />
            </>
          ),
        },
        {
          key: 'manage',
          label: t(lang, '时段管理', 'Period Management'),
          children: (
            <>
              <div className="ems-inline">
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setManage((arr) => [...arr, { key: Date.now(), start: '00:00', end: '01:00', kind: '谷' }])}>{t(lang, '新增时段', 'Add Period')}</Button>
                <Button size="small" className="panel-title-btn" onClick={() => setCfg('single')}>{t(lang, '单日配置', 'Single-Day Config')}</Button>
                <Button size="small" className="panel-title-btn" onClick={() => setCfg('batch')}>{t(lang, '批量配置', 'Batch Config')}</Button>
                <Button size="small" type="primary" onClick={() => message.success(t(lang, 'EMS 时段已保存', 'EMS period saved'))}>{t(lang, '保存', 'Save')}</Button>
              </div>
              <Table
                size="small" rowKey="key" pagination={false}
                columns={[
                  { title: t(lang, '时段说明', 'Period Description'), dataIndex: 'kind', width: 160, render: (v, r) => <Select size="small" value={v} options={PERIOD_KINDS.map((k) => ({ value: k, label: t(lang, k) }))} onChange={(x) => up(setManage, r.key, { kind: x })} style={{ width: 120 }} /> },
                  { title: t(lang, '开始时间', 'Start Time'), dataIndex: 'start', width: 130, render: (v, r) => <Input size="small" value={v} onChange={(e) => up(setManage, r.key, { start: e.target.value })} style={{ width: 90 }} /> },
                  { title: t(lang, '结束时间', 'End Time'), dataIndex: 'end', width: 130, render: (v, r) => <Input size="small" value={v} onChange={(e) => up(setManage, r.key, { end: e.target.value })} style={{ width: 90 }} /> },
                  { title: t(lang, '操作', 'Actions'), width: 90, render: (_, r) => <Button size="small" type="link" danger onClick={() => setManage((arr) => arr.filter((x) => x.key !== r.key))}>{t(lang, '删除', 'Delete')}</Button> },
                ]}
                dataSource={manage}
              />
            </>
          ),
        },
        {
          key: 'record',
          label: t(lang, '修改记录', 'Change Records'),
          children: (
            <Table
              size="small" rowKey="key" pagination={false}
              columns={[{ title: t(lang, '时间', 'Time'), dataIndex: 'time' }, { title: t(lang, '用户', 'User'), dataIndex: 'user' }, { title: t(lang, '变更内容', 'Change Content'), dataIndex: 'content' }]}
              dataSource={EMS_PERIOD_CHANGES}
            />
          ),
        },
      ]}
      />
      <Modal
        title={cfg === 'single' ? t(lang, '单日配置', 'Single-Day Config') : t(lang, '批量配置', 'Batch Config')}
        open={!!cfg}
        onCancel={() => setCfg(null)}
        okText={t(lang, '确定', 'OK')}
        cancelText={t(lang, '取消', 'Cancel')}
        onOk={() => { setCfg(null); message.success(cfg === 'single' ? t(lang, '当日时段配置已保存', 'Daily period config saved') : t(lang, '批量时段配置已保存', 'Batch period config saved')); }}
      >
        <div className="dev-kv-row"><span className="dev-kv-k">{cfg === 'single' ? t(lang, '配置日期', 'Config Date') : t(lang, '配置日期范围', 'Config Date Range')}</span>
          {cfg === 'single'
            ? <DatePicker size="small" style={{ width: 200 }} />
            : <DatePicker.RangePicker size="small" style={{ width: 260 }} />}
        </div>
      </Modal>
    </>
  );
}

// ---------- 8 电价管理 ----------
function Tariff() {
  const { lang } = useLang();
  const [rows, setRows] = useState(TARIFF_ROWS);
  return (
    <Panel title={t(lang, '电价管理', 'Tariff Management')} sub={t(lang, '配置分时电价（谷 / 平 / 峰 / 尖）', 'Configure TOU tariff (Valley / Flat / Peak / Sharp)')}
      extra={<Button size="small" className="panel-title-btn" type="primary" onClick={() => message.success(t(lang, '电价配置已保存', 'Tariff config saved'))}>{t(lang, '保存', 'Save')}</Button>}>
      <Table
        size="small" rowKey="key" pagination={false}
        columns={[
          { title: t(lang, '费率类型', 'Rate Type'), dataIndex: 'kind', render: (v) => <span className={`tariff-tag ${v === '谷' ? 'valley' : v === '平' ? 'flat' : v === '峰' ? 'peak' : 'sharp'}`}>{t(lang, v)}</span> },
          { title: t(lang, '时段', 'Period'), dataIndex: 'period' },
          { title: t(lang, '电价 (¥/kWh)', 'Price (¥/kWh)'), dataIndex: 'price', align: 'right', render: (v, r) => <InputNumber size="small" step={0.01} value={v} onChange={(x) => setRows((arr) => arr.map((y) => (y.key === r.key ? { ...y, price: x } : y)))} style={{ width: 120 }} /> },
        ]}
        dataSource={rows}
      />
    </Panel>
  );
}

// ---------- 9 二次口令 ----------
function SecPwd() {
  const { lang } = useLang();
  const policy = getSecPwdPolicy();
  const [form, setForm] = useState({ old: '', next: '', confirm: '' });
  return (
    <>
      <Panel title={t(lang, '二次口令', 'Secondary Password')} sub={t(lang, '可修改二次口令', 'Can modify the secondary password')}>
        <div className="kpi-grid ef2-kv">
          <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '口令规则', 'Password Rule')}</span><span className="dev-kv-v">{policy.rule}</span></div>
          <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '有效期', 'Validity')}</span><span className="dev-kv-v">{policy.validDays} {t(lang, '天', 'days')}</span></div>
          <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '连续错误锁定', 'Consecutive Fail Lock')}</span><span className="dev-kv-v">{policy.failLock} {t(lang, '次', 'times')}</span></div>
          <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '锁定时长', 'Lock Duration')}</span><span className="dev-kv-v">{policy.lockMinutes} {t(lang, '分钟', 'min')}</span></div>
          <div className="dev-kv-row" style={{ gridColumn: '1 / -1' }}><span className="dev-kv-k">{t(lang, '适用范围', 'Scope of Application')}</span><span className="dev-kv-v">{t(lang, '设备开关机 / 故障复归 / 消防联动环配置 / EMS 复归', 'Device on/off / fault reset / fire linkage ring config / EMS reset')}</span></div>
        </div>
        <div className="grid-2 grid-2-even" style={{ marginTop: 16 }}>
          <div>
            <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '原口令', 'Old Password')}</span><Input.Password value={form.old} onChange={(e) => setForm((s) => ({ ...s, old: e.target.value }))} /></div>
            <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '新口令', 'New Password')}</span><Input.Password value={form.next} onChange={(e) => setForm((s) => ({ ...s, next: e.target.value }))} /></div>
            <div className="dev-kv-row"><span className="dev-kv-k">{t(lang, '确认新口令', 'Confirm New Password')}</span><Input.Password value={form.confirm} onChange={(e) => setForm((s) => ({ ...s, confirm: e.target.value }))} /></div>
            <div className="ems-inline" style={{ marginTop: 14 }}>
              <Button type="primary" onClick={() => message.success(t(lang, '二次口令修改成功', 'Secondary password changed successfully'))}>{t(lang, '修改口令', 'Change Password')}</Button>
            </div>
          </div>
          <div className="ems-note" style={{ margin: 0 }}>{t(lang, '口令用于控制类指令（设备开关机、故障复归、消防联动环配置、EMS 复归等）的二次校验；请妥善保管。', 'The password is used for secondary verification of control commands (device on/off, fault reset, fire linkage ring config, EMS reset, etc.); keep it safe.')}</div>
        </div>
      </Panel>
      <Panel title={t(lang, '口令操作记录', 'Password Operation Records')} sub={t(lang, '最近修改 / 重置记录', 'Recent changes / resets')}>
        <Table
          size="small" rowKey="key" pagination={false}
          columns={[
            { title: t(lang, '时间', 'Time'), dataIndex: 'time' }, { title: t(lang, '用户', 'User'), dataIndex: 'user' }, { title: t(lang, '操作', 'Action'), dataIndex: 'action' },
            { title: 'IP', dataIndex: 'ip' },
            { title: t(lang, '结果', 'Result'), dataIndex: 'result', render: (v) => <Tag color={v === '成功' ? 'green' : 'red'}>{v}</Tag> },
          ]}
          dataSource={SEC_PWD_RECORDS}
        />
      </Panel>
    </>
  );
}

// ---------- 10 EMS 复归 ----------
function EmsReset() {
  const { lang } = useLang();
  const [scope, setScope] = useState('全站 EMS');
  const SCOPE_EN = { '全站 EMS': 'Entire Station EMS', 'PCS 故障复归': 'PCS Fault Reset', 'BMS 故障复归': 'BMS Fault Reset', '告警复归': 'Alarm Reset' };
  return (
    <>
      <Panel title={t(lang, 'EMS 复归', 'EMS Reset')} sub={t(lang, 'EMS 复归操作（需二次口令）', 'EMS reset operation (requires secondary password)')}>
        <div className="ems-inline">
          <span className="ems-inline-label">{t(lang, '复归范围', 'Reset Scope')}</span>
          <Select value={scope} onChange={setScope} style={{ width: 220 }} options={['全站 EMS', 'PCS 故障复归', 'BMS 故障复归', '告警复归'].map((v) => ({ value: v, label: t(lang, v, SCOPE_EN[v]) }))} />
          <Button danger type="primary" onClick={() => message.success(t(lang, `${scope} 复归指令已下发`, `${scope} reset command issued`))}>{t(lang, '执行复归', 'Execute Reset')}</Button>
        </div>
        <div className="ems-note">{t(lang, '复归将清除故障闭锁状态；执行前需通过二次口令校验。', 'Reset will clear fault latch states; secondary password verification is required before execution.')}</div>
      </Panel>
      <Panel title={t(lang, '复归记录', 'Reset Records')} sub={t(lang, 'EMS 复归操作历史', 'EMS reset operation history')}>
        <Table
          size="small" rowKey="key" pagination={false}
          columns={[
            { title: t(lang, '时间', 'Time'), dataIndex: 'time' }, { title: t(lang, '范围', 'Scope'), dataIndex: 'scope' }, { title: t(lang, '用户', 'User'), dataIndex: 'user' },
            { title: t(lang, '复归前状态', 'Pre-Reset State'), dataIndex: 'before' },
            { title: t(lang, '结果', 'Result'), dataIndex: 'result', render: (v) => <Tag color="green">{v}</Tag> },
          ]}
          dataSource={EMS_RESET_RECORDS}
        />
      </Panel>
    </>
  );
}

// ---------- 11 电量矫正 ----------
function Correction({ st }) {
  const { lang } = useLang();
  const dev = getDevices(st.id);
  const [form, setForm] = useState({ meter: dev.meters[0].name, after: dev.meters[0].ep, reason: '' });
  return (
    <>
      <Panel title={t(lang, '电量矫正', 'Energy Correction')} sub={t(lang, '兼容电表电量数据错误，可手动矫正', 'Compensates meter energy data errors; manual correction available')}
        extra={<Button size="small" className="panel-title-btn" type="primary" onClick={() => message.success(t(lang, '电量矫正已提交', 'Correction submitted'))}>{t(lang, '提交矫正', 'Submit Correction')}</Button>}>
        <div className="ems-inline">
          <span className="ems-inline-label">{t(lang, '电表', 'Meter')}</span>
          <Select value={form.meter} onChange={(v) => setForm((s) => ({ ...s, meter: v }))} style={{ width: 220 }} options={dev.meters.map((m) => ({ value: m.name, label: m.name }))} />
          <span className="ems-inline-label">{t(lang, '矫正后数值 (kWh)', 'Corrected Value (kWh)')}</span>
          <InputNumber value={form.after} onChange={(v) => setForm((s) => ({ ...s, after: v }))} style={{ width: 160 }} />
          <span className="ems-inline-label">{t(lang, '原因', 'Reason')}</span>
          <Input value={form.reason} onChange={(e) => setForm((s) => ({ ...s, reason: e.target.value }))} placeholder={t(lang, '如：通信中断补录', 'e.g., backfill after communication interruption')} style={{ width: 220 }} />
        </div>
        <div className="ems-note">{t(lang, '矫正仅修正因通信中断、变比错误等造成的电量偏差。', 'Correction only fixes energy deviations caused by communication interruptions, ratio errors, etc.')}</div>
      </Panel>
      <Panel title={t(lang, '矫正记录', 'Correction Records')} sub={t(lang, '历史电量矫正', 'Historical energy corrections')}>
        <Table
          size="small" rowKey="key" pagination={false}
          columns={[
            { title: t(lang, '时间', 'Time'), dataIndex: 'time' }, { title: t(lang, '电表', 'Meter'), dataIndex: 'meter' },
            { title: t(lang, '矫正前 (kWh)', 'Before (kWh)'), dataIndex: 'before', align: 'right' }, { title: t(lang, '矫正后 (kWh)', 'After (kWh)'), dataIndex: 'after', align: 'right' },
            { title: t(lang, '原因', 'Reason'), dataIndex: 'reason' }, { title: t(lang, '操作人', 'Operator'), dataIndex: 'operator' },
          ]}
          dataSource={CORRECTIONS}
        />
      </Panel>
      <Panel title={t(lang, '收益重算记录', 'Revenue Recalculation Records')} sub={t(lang, '电量矫正后的收益重算', 'Revenue recalculation after energy correction')}>
        <Table
          size="small" rowKey="key" pagination={false}
          columns={[
            { title: t(lang, '时间', 'Time'), dataIndex: 'time' }, { title: t(lang, '重算区间', 'Recalc Range'), dataIndex: 'range' },
            { title: t(lang, '重算前 (元)', 'Before (CNY)'), dataIndex: 'before', align: 'right' }, { title: t(lang, '重算后 (元)', 'After (CNY)'), dataIndex: 'after', align: 'right' },
            { title: t(lang, '操作人', 'Operator'), dataIndex: 'operator' },
          ]}
          dataSource={RECALC_RECORDS}
        />
      </Panel>
    </>
  );
}

// ---------- 12 语言 ----------
function Lang() {
  const { lang, setLang } = useLang();
  return (
    <Panel title={t(lang, '语言', 'Language')} sub={t(lang, '切换界面语言，支持中英文', 'Switch interface language, supports Chinese & English')}>
      <Radio.Group value={lang} onChange={(e) => { setLang(e.target.value); message.success(e.target.value === 'zh' ? '已切换为中文' : 'Switched to English'); }}>
        <Radio value="zh">{t(lang, '中文', 'Chinese')}</Radio>
        <Radio value="en">English</Radio>
      </Radio.Group>
    </Panel>
  );
}

// ---------- 13 告警清理 ----------
function AlarmClean() {
  const { lang } = useLang();
  const [scope, setScope] = useState('已恢复告警');
  const SCOPE_EN = { '已恢复告警': 'Recovered Alarms', '30 天前历史告警': 'Alarms Older Than 30 Days', '90 天前历史告警': 'Alarms Older Than 90 Days', '全部历史告警': 'All Historical Alarms' };
  return (
    <>
      <Panel title={t(lang, '告警清理', 'Alarm Cleanup')} sub={t(lang, '清理固定时间段内的告警', 'Clean alarms within a fixed time range')}
        extra={<Button size="small" className="panel-title-btn" danger type="primary" onClick={() => message.success(t(lang, '告警清理任务已执行', 'Alarm cleanup task executed'))}>{t(lang, '执行清理', 'Execute Cleanup')}</Button>}>
        <div className="ems-inline">
          <span className="ems-inline-label">{t(lang, '清理范围', 'Cleanup Scope')}</span>
          <Select value={scope} onChange={setScope} style={{ width: 220 }} options={['已恢复告警', '30 天前历史告警', '90 天前历史告警', '全部历史告警'].map((v) => ({ value: v, label: t(lang, v, SCOPE_EN[v]) }))} />
        </div>
        <div className="ems-note">{t(lang, '清理仅作用于历史告警，不影响今日告警。', 'Cleanup only affects historical alarms; today\'s alarms are unaffected.')}</div>
      </Panel>
      <Panel title={t(lang, '清理记录', 'Cleanup Records')} sub={t(lang, '历史告警清理', 'Historical alarm cleanup')}>
        <Table
          size="small" rowKey="key" pagination={false}
          columns={[
            { title: t(lang, '时间', 'Time'), dataIndex: 'time' }, { title: t(lang, '清理范围', 'Cleanup Scope'), dataIndex: 'scope' },
            { title: t(lang, '清理条数', 'Cleaned Count'), dataIndex: 'count', align: 'right' }, { title: t(lang, '操作人', 'Operator'), dataIndex: 'operator' },
          ]}
          dataSource={CLEAR_RECORDS}
        />
      </Panel>
    </>
  );
}

export default function EmsSystem({ station }) {
  const { lang } = useLang();
  const st = station || getStation('xz');
  const items = [
    { key: 'basic', label: t(lang, '基本信息管理', 'Basic Info Management'), children: <Basic st={st} /> },
    { key: 'sys', label: t(lang, '系统配置', 'System Configuration'), children: <SysConfig st={st} /> },
    { key: 'device', label: t(lang, '设备管理', 'Device Management'), children: <DeviceManage st={st} /> },
    { key: 'hv', label: t(lang, '强电管理', 'HV Management'), children: <HvManage st={st} /> },
    { key: 'topo', label: t(lang, '拓扑图配置', 'Topology Config'), children: <TopoConfig /> },
    { key: 'log', label: t(lang, '日志管理', 'Log Management'), children: <Logs /> },
    { key: 'period', label: t(lang, 'EMS 时段管理', 'EMS Period Management'), children: <EmsPeriods /> },
    { key: 'tariff', label: t(lang, '电价管理', 'Tariff Management'), children: <Tariff /> },
    { key: 'pwd', label: t(lang, '二次口令', 'Secondary Password'), children: <SecPwd /> },
    { key: 'reset', label: t(lang, 'EMS 复归', 'EMS Reset'), children: <EmsReset /> },
    { key: 'correct', label: t(lang, '电量矫正', 'Energy Correction'), children: <Correction st={st} /> },
    { key: 'lang', label: t(lang, '语言', 'Language'), children: <Lang /> },
    { key: 'clean', label: t(lang, '告警清理', 'Alarm Cleanup'), children: <AlarmClean /> },
  ];
  // 子页切换对齐光储充「控制中心」顶部胶囊 Tabs
  return <ScrollTabs items={items} />;
}
